/**
 * Add watermark to image using backend API (Sharp library)
 * This provides better performance and consistency compared to client-side canvas
 * 
 * @param {File} file - The image file to watermark
 * @param {string} watermarkText - The text to use as watermark (user-provided)
 * @returns {Promise<File>} - Promise that resolves to watermarked image file
 */
export const addWatermarkToImage = async (file, watermarkText) => {
  try {
    // If no watermark text provided or file is not an image, return original file
    if (!watermarkText || !file.type.startsWith('image/')) {
      return file;
    }

    // Create FormData to send file and userName
    const formData = new FormData();
    formData.append('image', file);
    formData.append('watermarkText', watermarkText);

    // Get API base URL - use environment variable or default to relative path
    const apiBaseUrl = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_API_SERVER_PORT
      ? `http://localhost:${process.env.REACT_APP_DEV_API_SERVER_PORT}`
      : '';

    // Call backend API
    const response = await fetch(`${apiBaseUrl}/api/add-watermark`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.warn('Backend watermark failed:', error);
      return file; // Return original file on error
    }

    // Convert response to blob
    const blob = await response.blob();

    // Create new File from blob
    const watermarkedFile = new File([blob], file.name, {
      type: file.type,
      lastModified: Date.now(),
    });

    return watermarkedFile;
  } catch (error) {
    console.warn('Error calling backend watermark API, using original image:', error);
    return file; // Return original file on error
  }
};

/**
 * Convert WebP and AVIF images to JPEG format
 * 
 * @param {File} file - The image file to convert
 * @param {number} quality - JPEG quality (0.1 to 1.0, default 0.9)
 * @returns {Promise<File>} - Promise that resolves to converted JPEG file or original file if no conversion needed
 */
export const convertToJpeg = async (file, quality = 0.8) => {
  try {
    // Check if file is WebP or AVIF
    if (!file.type.includes('webp') && !file.type.includes('avif')) {
      return file; // Return original file if not WebP or AVIF
    }

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Clean up object URL immediately
        URL.revokeObjectURL(img.src);

        try {
          // Optimize canvas size for large images
          const maxWidth = 1920;
          const maxHeight = 1080;

          let { width, height } = img;

          // Scale down large images for faster processing
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;

          // Use better image rendering for quality and speed
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw image on canvas with optimized size
          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas to blob in JPEG format with optimized quality
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create new filename with .jpg extension
                const originalName = file.name.replace(/\.(webp|avif)$/i, '');
                const jpegFile = new File([blob], `${originalName}.jpg`, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(jpegFile);
              } else {
                console.warn('Failed to convert image to JPEG, using original');
                resolve(file);
              }
            },
            'image/jpeg',
            quality
          );
        } catch (error) {
          console.warn('Error during image conversion:', error);
          resolve(file);
        }
      };

      img.onerror = () => {
        console.warn('Failed to load image for conversion, using original');
        resolve(file);
      };

      // Create object URL and load image
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    });
  } catch (error) {
    console.warn('Error converting image to JPEG:', error);
    return file; // Return original file on error
  }
};

/**
 * Add watermark to multiple images using backend API
 * 
 * @param {File[]} files - Array of image files to watermark
 * @param {string} userName - The user's name to use as watermark
 * @returns {Promise<File[]>} - Promise that resolves to array of watermarked image files
 */
export const addWatermarkToImagesBackend = async (files, userName) => {
  try {
    const watermarkedFiles = await Promise.all(
      files.map(file => addWatermarkToImage(file, userName))
    );
    return watermarkedFiles;
  } catch (error) {
    console.warn('Error watermarking multiple images:', error);
    return files; // Return original files on error
  }
};
